import type {ToolCallMessagePartComponent} from '@assistant-ui/react';
import {Icon} from '@wandb/components';
import React, {useState} from 'react';

import {WBAgentQuestionCarousel} from '../WBAgentQuestionCarousel';
import {useToolInteraction} from './useToolInteraction';

type AskUserQuestion = {
  question?: string;
  options?: string[];
};

type AskUserArgs = {
  questions?: AskUserQuestion[];
};

/**
 * AskUserToolUI renders an ask_user call. While the call is the one awaiting a
 * response, it presents the guided answer carousel inline; otherwise (a past
 * turn, or after the user answered or typed instead) it renders a compact
 * historical indicator.
 */
export const AskUserToolUI: ToolCallMessagePartComponent<AskUserArgs> = ({
  toolCallId,
  args,
}) => {
  const {isActionable, payload, isDisabled, submit} =
    useToolInteraction(toolCallId);

  if (isActionable && payload?.kind === 'questions') {
    return (
      <WBAgentQuestionCarousel
        questions={payload.questions}
        isDisabled={isDisabled}
        onSubmit={answers => submit({answers})}
      />
    );
  }

  return <AskUserIndicator args={args} />;
};

/**
 * AskUserIndicator is the historical rendering of a completed ask_user call — a
 * small indicator that the agent posed question(s), expandable to show them.
 */
const AskUserIndicator: React.FC<{args?: AskUserArgs}> = ({args}) => {
  const [expanded, setExpanded] = useState(false);
  const questions = Array.isArray(args?.questions) ? args.questions : [];
  const count = questions.length;
  const label = count > 1 ? `Asked ${count} questions` : 'Asked a question';
  const canExpand = count > 0;

  return (
    <div className="flex flex-col gap-8 py-4">
      <button
        type="button"
        disabled={!canExpand}
        onClick={() => setExpanded(e => !e)}
        className="flex w-full items-center gap-8 -mx-6 px-6 py-4 text-left transition-colors rounded-md bg-transparent hover:bg-moon-100 cursor-pointer text-moon-800 disabled:cursor-default disabled:hover:bg-transparent">
        <span className="inline-flex shrink-0">
          <Icon icon="forum-chat-bubble" size="sm" color="moon" />
        </span>
        <span className="text-left leading-[21px] font-semibold text-sm">
          {label}
        </span>
        <span className="flex-1" />
        {canExpand && (
          <span
            className={`inline-flex shrink-0 text-moon-450 transition-[rotate] duration-[220ms] ${
              expanded ? 'rotate-90' : 'rotate-0'
            }`}>
            <Icon icon="chevron-next" size="sm" />
          </span>
        )}
      </button>
      {expanded && (
        <div className="flex flex-col gap-8 border-l-2 border-moon-200 pl-12">
          {questions.map((q, i) => (
            // oxlint-disable-next-line react/no-array-index-key
            <div key={i} className="flex flex-col gap-4">
              <span className="text-sm text-moon-700">{q.question}</span>
              {Array.isArray(q.options) && q.options.length > 0 && (
                <div className="flex flex-wrap gap-4">
                  {q.options.map(option => (
                    <span
                      key={option}
                      className="rounded bg-moon-100 px-6 py-1 text-xs text-moon-600">
                      {option}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
